import React, { useMemo, useState, useRef, useEffect } from "react";
import "./styles.sass";

const CustomDropdown = ({ value, onChange, options = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOpt = options.find((o) => o.value === value) || options[0];

  return (
    <div className="custom_history_dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`custom_history_dropdown_trigger ${isOpen ? "is_open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOpt?.label}</span>
        <svg
          className={`dropdown_chevron ${isOpen ? "rotate_chevron" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#64748B"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="custom_history_dropdown_menu">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={`custom_history_dropdown_item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {isSelected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const QuizHistroyDetails = ({ historyData = [], onBack }) => {
  const [selectedType, setSelectedType] = useState("all");
  const [sortOrder, setSortOrder] = useState("recent");

  const typeOptions = [
    { value: "all", label: "All Types" },
    { value: "live", label: "Live" },
    { value: "async", label: "Async" },
  ];

  const sortOptions = [
    { value: "recent", label: "Recent First" },
    { value: "old", label: "Old First" },
  ];

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const getDuration = (joinedAt, submittedAt) => {
    if (!joinedAt || !submittedAt) return "-";
    try {
      const start = new Date(joinedAt);
      const end = new Date(submittedAt);
      const diffMs = end - start;
      if (isNaN(diffMs) || diffMs < 0) return "-";
      const mins = Math.floor(diffMs / 1000 / 60);
      return `${mins} min${mins !== 1 ? "s" : ""}`;
    } catch {
      return "-";
    }
  };

  const filteredHistory = useMemo(() => {
    let data = [...(historyData || [])];

    // filter by mode
    if (selectedType !== "all") {
      data = data.filter((item) => item?.mode === selectedType);
    }

    // sort
    data.sort((a, b) => {
      const dateA = new Date(a?.joined_at || 0).getTime();
      const dateB = new Date(b?.joined_at || 0).getTime();
      return sortOrder === "recent" ? dateB - dateA : dateA - dateB;
    });

    return data;
  }, [historyData, selectedType, sortOrder]);

  const stats = useMemo(() => {
    const total = historyData?.length || 0;
    const maxScore = historyData?.reduce((max, i) => Math.max(max, Number(i?.my_score) || 0), 0) || 0;
    return { total, maxScore };
  }, [historyData]);

  return (
    <section className="async_quiz_histroy_container">
      {/* Header section */}
      <div className="quiz_histroy_heading_wrapper">
        <div className="quiz_history_header_container">
          <div className="quiz_history_title_row">
            <button
              className="quiz_history_back_btn"
              onClick={handleBack}
              title="Back to Objective Test Screen"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>

            <div className="quiz_history_icon_badge">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <h2>Quiz History</h2>
              <p>Review your past performance and track your learning progress.</p>
            </div>
          </div>
        </div>

        <div className="quiz_histroy_heading_left_wrapper">
          {stats.total > 0 && (
            <div className="quiz_history_stat_chip">
              <span className="chip_label">Total Attempted:</span>
              <span className="chip_value">{stats.total}</span>
            </div>
          )}

          <CustomDropdown
            value={selectedType}
            onChange={setSelectedType}
            options={typeOptions}
          />

          <CustomDropdown
            value={sortOrder}
            onChange={setSortOrder}
            options={sortOptions}
          />
        </div>
      </div>

      {/* History Cards Container */}
      <div className="quiz_history_scroll_area">
        {filteredHistory.length > 0 ? (
          filteredHistory.map((item, index) => {
            const isAsync = item?.mode === "async";
            const score = Number(item?.my_score) || 0;

            return (
              <div
                className={`quiz_histroy_content_details_wrapper ${isAsync ? "mode_async" : "mode_live"}`}
                key={item?.room_code || index}
              >
                <div className="quiz_history_main_info">
                  <div className="quiz_histroy_content_header_wrapper">
                    <span className="quiz_room_code_badge">
                      #{item?.room_code || "---"}
                    </span>

                    <span className={`quiz_histroy_async_wrapper ${isAsync ? "async_pill" : "live_pill"}`}>
                      <span className="mode_dot"></span>
                      {isAsync ? "Async" : "Live"}
                    </span>
                  </div>

                  <h3 className="quiz_subject_title">
                    {item?.filters?.subject || item?.test_name || "Quiz Session"}
                  </h3>

                  <div className="quiz_histroy_content_footer_wrapper">
                    {item?.filters?.lesson && (
                      <>
                        <div className="meta_item">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          <span>{item?.filters?.lesson}</span>
                        </div>
                        <span className="quiz_histroy_dot_wrapper"></span>
                      </>
                    )}

                    <div className="meta_item">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>{formatDate(item?.joined_at)}</span>
                    </div>

                    <span className="quiz_histroy_dot_wrapper"></span>

                    <div className="meta_item">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>{getDuration(item?.joined_at, item?.submitted_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="quiz_histroy_left_details_wrapper">
                  <div className={`score_badge_box ${score > 0 ? "positive_score" : "zero_score"}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                      <path d="M4 22h16" />
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
                    </svg>
                    <h3>{score} pts</h3>
                  </div>

                  <div className="rank_badge_box">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <h4>
                      Rank #{item?.my_rank || 1} of {item?.total_players || 1}
                    </h4>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="history_not_found_data_wrapper">
            <div className="empty_icon_box">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h4>No Quiz History Found</h4>
            <p>Your completed quizzes and performance records will appear here.</p>
          </div>
        )}
      </div>
    </section>
  );
};
